import { JSONSchema7 } from 'json-schema';
import { toTitleCase } from '../stringMethods';

/** Shape shared by AJV's ErrorObject and FormHell's SchemaFormValidationError. */
export type RawValidationError = {
    keyword?: string;
    instancePath?: string;
    schemaPath?: string;
    message?: string;
    params?: Record<string, unknown>;
};

export type SchemaValidationError = {
    keyword: string;
    instancePath: string;
    property: string;
    message: string;
    params: Record<string, unknown>;
    schemaPath: string;
};

export type ErrorsByPointer = Record<string, string[]>;

export type MappedValidationErrors = {
    errors: SchemaValidationError[];
    errorsByPointer: ErrorsByPointer;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Drops keys whose value is an empty string or undefined so that AJV's `required`
 * flags them as missing. Array elements are kept as they are.
 */
export const stripEmptyValues = (data: any): any => {
    if (Array.isArray(data)) {
        return data.map(stripEmptyValues);
    }
    if (isObject(data)) {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
            if (value === undefined || (typeof value === 'string' && value.trim() === '')) {
                continue;
            }
            result[key] = stripEmptyValues(value);
        }
        return result;
    }
    return data;
};

const MESSAGE_REWRITES: Array<[RegExp, string]> = [
    [/^must have required property (.+)$/i, '$1 is required'],
    [/should be equal to one of the allowed values/gi, 'is not a valid option'],
    [/should match pattern/gi, 'has invalid format'],
    [/should NOT be shorter than/gi, 'is too short (minimum'],
    [/should NOT be longer than/gi, 'is too long (maximum'],
    [/should be/gi, 'must be'],
];

export const formatFieldPath = (path: string): string => {
    if (!path) return '';
    const formatted = path
        .replace(/^\./, '')
        .split('.')
        .map(part => part.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase()).trim())
        .join(' > ');
    return formatted ? ` - ${formatted}` : '';
};

export const formatErrorMessage = (message: string): string => {
    const rewritten = MESSAGE_REWRITES
        .reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), message)
        .replace(/'([^']+)'/g, '"$1"');
    return rewritten.charAt(0).toUpperCase() + rewritten.slice(1);
};

const escapePointerSegment = (segment: string) => segment.replace(/~/g, '~0').replace(/\//g, '~1');
const unescapePointerSegment = (segment: string) => segment.replace(/~1/g, '/').replace(/~0/g, '~');

const resolveLocalRef = (node: Record<string, any>, root: JSONSchema7): Record<string, any> => {
    if (typeof node.$ref !== 'string' || !node.$ref.startsWith('#')) {
        return node;
    }
    let target: any = root;
    for (const segment of node.$ref.slice(1).split('/').filter(Boolean).map(unescapePointerSegment)) {
        if (!isObject(target) || !(segment in target)) {
            return node;
        }
        target = target[segment];
    }
    return isObject(target) ? { ...target, ...node, $ref: undefined } : node;
};

/** Walks `properties` / `prefixItems` / `items` (resolving local $refs) to the schema at a data pointer. */
export const resolveSchemaAtPointer = (schema: JSONSchema7, pointer: string): Record<string, any> | undefined => {
    let node: Record<string, any> = resolveLocalRef(schema as Record<string, any>, schema);
    for (const token of pointer.split('/').slice(1).map(unescapePointerSegment)) {
        let next: unknown;
        if (isObject(node.properties) && token in node.properties) {
            next = node.properties[token];
        } else if (/^\d+$/.test(token)) {
            next = Array.isArray(node.prefixItems) ? node.prefixItems[Number(token)] ?? node.items : node.items;
        }
        if (!isObject(next)) {
            return undefined;
        }
        node = resolveLocalRef(next, schema);
    }
    return node;
};

export const resolveTitle = (schema: JSONSchema7, pointer: string, key: string): string => {
    const title = resolveSchemaAtPointer(schema, pointer)?.title;
    return typeof title === 'string' && title.trim() ? title : toTitleCase(key);
};

/** FormHell prefixes AJV messages with `data<instancePath> `; strip exactly that. */
const stripFormHellPrefix = (message: string, instancePath: string): string => {
    const prefix = `data${instancePath} `;
    return message.startsWith(prefix) ? message.slice(prefix.length) : message;
};


export const mapValidationErrors = (errors: RawValidationError[] | null | undefined, schema: JSONSchema7): MappedValidationErrors => {
    const mapped: SchemaValidationError[] = (errors || []).map((error) => {
        const instancePath = error.instancePath ?? '';
        const params = (error.params ?? {}) as Record<string, unknown>;
        const keyword = error.keyword ?? '';
        let pointer = instancePath;
        let message = stripFormHellPrefix(error.message || 'is invalid', instancePath);

        if (keyword === 'required' && typeof params.missingProperty === 'string') {
            const missing = params.missingProperty;
            pointer = `${instancePath}/${escapePointerSegment(missing)}`;
            message = message.replace(`'${missing}'`, `'${resolveTitle(schema, pointer, missing)}'`);
        } else if (keyword === 'additionalProperties' && typeof params.additionalProperty === 'string') {
            pointer = `${instancePath}/${escapePointerSegment(params.additionalProperty)}`;
        }

        return {
            keyword,
            instancePath: pointer,
            property: pointer.split('/').slice(1).map(unescapePointerSegment).join('.'),
            message: formatErrorMessage(message),
            params,
            schemaPath: error.schemaPath ?? '',
        };
    });

    const errorsByPointer: ErrorsByPointer = {};
    mapped.forEach(({ instancePath, message }) => {
        errorsByPointer[instancePath] = [...(errorsByPointer[instancePath] || []), message];
    });
    return { errors: mapped, errorsByPointer };
};
